import { h } from 'vue'

import { FormViewFormModeType } from '@baserow/modules/database/formViewModeTypes'
import FormViewModeForm from '@baserow/modules/database/components/view/form/FormViewModeForm'
import FormViewModePreviewForm from '@baserow/modules/database/components/view/form/FormViewModePreviewForm'
import FormSafetyAlert from '@saas/components/formSafety/FormSafetyAlert'

/**
 * Extends the original component instead of wrapping it because the public form page
 * accesses the field refs via `this.$refs.form.$refs`. Rendering the original
 * template in the same instance keeps those refs working.
 */
function withFormSafetyAlert(Original, mode, getHeading) {
  const component = {
    name: `${Original.name}WithFormSafetyAlert`,
    extends: Original,
    render(...args) {
      const alertProps =
        mode === 'public'
          ? { mode, slug: this.$route.params.slug }
          : {
              mode,
              slug: this.view.slug,
              refreshKey: JSON.stringify([
                this.view.public,
                this.fieldOptions,
                this.fields.map((field) => field.name),
              ]),
            }
      alertProps.getTarget = () => getHeading(this)
      return [h(FormSafetyAlert, alertProps), Original.render.apply(this, args)]
    },
  }

  // During SSR the compiled template is available as `ssrRender` instead of
  // `render`. The alert only fetches its state after mounting, so it's rendered as an
  // empty comment, which matches the fragment that `render` creates when hydrating.
  // The attributes aren't passed on because `render` returns a fragment, which
  // doesn't receive them in the browser either.
  if (Original.ssrRender) {
    component.ssrRender = (ctx, push, parent, attrs, ...rest) => {
      push('<!--[--><!---->')
      Original.ssrRender(ctx, push, parent, undefined, ...rest)
      push('<!--]-->')
    }
  }

  return component
}

/**
 * The heading contains the title and description, and the alert is added at the end
 * of it. Core doesn't have a ref on the heading, so it's found via the nearest refs.
 * On the public form, the heading is directly followed by the first field.
 */
function getPublicFormHeading(component) {
  const firstField = component.visibleFields[0]
  const fieldRef = firstField && component.$refs[`field-${firstField.field.id}`]
  const fieldComponent = Array.isArray(fieldRef) ? fieldRef[0] : fieldRef
  return fieldComponent?.$el.previousElementSibling
}

/**
 * In the editor, the title is an `Editable` inside the heading's `h1`.
 */
function getPreviewFormHeading(component) {
  return component.$refs.title?.$el.parentElement.parentElement
}

const FormViewModeFormWithAlert = withFormSafetyAlert(
  FormViewModeForm,
  'public',
  getPublicFormHeading
)
const FormViewModePreviewFormWithAlert = withFormSafetyAlert(
  FormViewModePreviewForm,
  'editor',
  getPreviewFormHeading
)
/**
 * Only the form mode gets the alert because the survey mode requires a paid plan,
 * and paid workspaces never see the alert.
 */
export class SaaSFormViewFormModeType extends FormViewFormModeType {
  getFormComponent() {
    return FormViewModeFormWithAlert
  }

  getPreviewComponent() {
    return FormViewModePreviewFormWithAlert
  }
}
